/**
 * The pre-workout check-in: "How did you sleep?", "How do you feel?", "Is anything hurting?", asked
 * before every workout (decided 2026-10-04).
 *
 * Answers travel as short ids, never as the words on the buttons, so rewording a button doesn't
 * change what an old answer meant. The words for each id live here and nowhere else.
 */

export type SleepAnswer = "bad" | "ok" | "great";
export type FeelAnswer = "rough" | "ok" | "good";
export type PainAnswer = "no" | "yes" | "unsure";
export type PainArea = "ankle" | "knee" | "hip" | "back" | "shoulder" | "other";

/** What the walkthrough sends, in the request's camelCase. */
export interface CheckinAnswers {
  sleep: SleepAnswer;
  feel: FeelAnswer;
  pain: PainAnswer;
  painAreas: PainArea[];
  painNote: string;
}

/** What the server stores and sends back on a session or a draft. */
export interface StoredCheckin {
  sleep: SleepAnswer;
  feel: FeelAnswer;
  pain: PainAnswer;
  pain_areas: PainArea[];
  pain_note: string | null;
}

/** How a stored answer reads to a coach: good, so-so, or worth a look. */
export type Tone = "good" | "so-so" | "poor";

export interface Question<T extends string> {
  key: "sleep" | "feel" | "pain";
  label: string;
  /** Worst first, matching the mock: the athlete reads left to right from "not good" to "good". */
  options: { value: T; text: string; tone: Tone }[];
}

export const SLEEP_QUESTION: Question<SleepAnswer> = {
  key: "sleep",
  label: "How did you sleep?",
  options: [
    { value: "bad", text: "Badly", tone: "poor" },
    { value: "ok", text: "OK", tone: "so-so" },
    { value: "great", text: "Great", tone: "good" },
  ],
};

export const FEEL_QUESTION: Question<FeelAnswer> = {
  key: "feel",
  label: "How do you feel?",
  options: [
    { value: "rough", text: "Not great", tone: "poor" },
    { value: "ok", text: "OK", tone: "so-so" },
    { value: "good", text: "Good", tone: "good" },
  ],
};

export const PAIN_QUESTION: Question<PainAnswer> = {
  key: "pain",
  label: "Is anything hurting?",
  options: [
    { value: "no", text: "No", tone: "good" },
    { value: "yes", text: "Yes", tone: "poor" },
    { value: "unsure", text: "Not sure", tone: "so-so" },
  ],
};

export const PAIN_AREAS: { value: PainArea; text: string }[] = [
  { value: "ankle", text: "Ankle" },
  { value: "knee", text: "Knee" },
  { value: "hip", text: "Hip" },
  { value: "back", text: "Back" },
  { value: "shoulder", text: "Shoulder" },
  { value: "other", text: "Somewhere else" },
];

/** The stored form, for sending a resumed workout's check-in back with its session. */
export function toAnswers(stored: StoredCheckin): CheckinAnswers {
  return {
    sleep: stored.sleep,
    feel: stored.feel,
    pain: stored.pain,
    painAreas: stored.pain_areas ?? [],
    painNote: stored.pain_note ?? "",
  };
}

/** "knee and back", for the coach's one-line summary. */
export function describeAreas(areas: PainArea[]): string {
  const words = areas.map((area) => (area === "other" ? "somewhere else" : area));
  if (words.length <= 1) return words[0] ?? "";
  return `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
}

/**
 * The three answers as short tags for a coach: "Slept OK", "Feeling not great", "Knee hurts". Each
 * carries its tone, so the coach can scan colour before reading words.
 */
export function checkinTags(checkin: StoredCheckin): { text: string; tone: Tone }[] {
  const sleep = SLEEP_QUESTION.options.find((option) => option.value === checkin.sleep);
  const feel = FEEL_QUESTION.options.find((option) => option.value === checkin.feel);
  const painTone = PAIN_QUESTION.options.find((option) => option.value === checkin.pain)?.tone ?? "so-so";
  const areas = describeAreas(checkin.pain_areas ?? []);
  const painText =
    checkin.pain === "no"
      ? "Nothing hurts"
      : checkin.pain === "unsure"
        ? "Not sure if anything hurts"
        : areas
          ? `${areas.charAt(0).toUpperCase()}${areas.slice(1)} hurts`
          : "Something hurts";

  return [
    ...(sleep ? [{ text: `Slept ${sleep.text.toLowerCase() === "ok" ? "OK" : sleep.text.toLowerCase()}`, tone: sleep.tone }] : []),
    ...(feel ? [{ text: `Feeling ${feel.text === "OK" ? "OK" : feel.text.toLowerCase()}`, tone: feel.tone }] : []),
    { text: painText, tone: painTone },
  ];
}

/** The tone of one answer, for the coach's 4-week grid. */
export function toneOf(key: "sleep" | "feel" | "pain", checkin: StoredCheckin): Tone {
  const question = key === "sleep" ? SLEEP_QUESTION : key === "feel" ? FEEL_QUESTION : PAIN_QUESTION;
  return (question.options as { value: string; tone: Tone }[]).find((option) => option.value === checkin[key])?.tone ?? "so-so";
}
